import { useCallback } from 'react';
import { useEditor, activeMasterOf } from '../context/EditorContext';
import { hasCrop, fitRect, loadNaturalSize, clearCropPatch, refitCrop, cropAspectById, cropToAspect } from '../lib/pictureCrop';
import { isCropShape, cropShapeLabel } from '../lib/cropShape';
import { toast } from 'sonner';

const activeObjectsOf = (s) => (s.viewMode === 'master' ? activeMasterOf(s)?.objects || [] : [...(s.pages[s.currentPageIndex]?.objects || []), ...(s.scratch?.objects || [])]);

/**
 * Publisher-style in-place cropping: enter / leave crop mode (`state.cropEditId`), Fit, Fill and Clear Crop.
 * Entering converts the picture's object-fit placement into an explicit `properties.crop` rectangle first (needs the
 * picture's natural size, loaded once), so the handles have something to move.
 */
export const useCrop = () => {
  const { state, stateRef, dispatch, rawDispatch } = useEditor();
  const find = useCallback((id) => activeObjectsOf(stateRef.current).find((o) => o.id === id), [stateRef]);
  const target = useCallback((objectId) => {
    const s = stateRef.current;
    const id = objectId || (s.selectedObjectIds.length === 1 ? s.selectedObjectIds[0] : null);
    return id ? find(id) : null;
  }, [stateRef, find]);

  const ensureCrop = useCallback(async (o) => {
    if (hasCrop(o.properties)) return o.properties.crop;
    const nat = await loadNaturalSize(o.properties.src);
    const crop = fitRect(o.width, o.height, nat?.w, nat?.h, o.properties.objectFit || 'cover');
    dispatch({ type: 'UPDATE_OBJECT_PROPS', payload: { ids: [o.id], props: { crop } } });
    return crop;
  }, [dispatch]);

  /** Start cropping `objectId` (defaults to the single selected picture). */
  const enter = useCallback(async (objectId) => {
    const o = target(objectId);
    if (!o || o.type !== 'image') { toast.info('Select a picture first.'); return false; }
    if (!o.properties?.src) { toast.info('This frame has no picture to crop yet.'); return false; }
    if (o.locked) { toast.info('Unlock the picture to crop it.'); return false; }
    if (stateRef.current.viewMode !== 'master' && stateRef.current.selectedObjectIds.join() !== o.id) dispatch({ type: 'SELECT_OBJECT', payload: { id: o.id } });
    await ensureCrop(o);
    rawDispatch({ type: 'SET_CROP_EDIT', payload: o.id });
    toast.info('Drag the black handles to crop · drag the picture to move it inside the frame · Esc or Done to finish', { id: 'crop-hint', duration: 4000 });
    return true;
  }, [target, ensureCrop, dispatch, rawDispatch, stateRef]);

  const exit = useCallback(() => rawDispatch({ type: 'SET_CROP_EDIT', payload: null }), [rawDispatch]);

  const toggle = useCallback((objectId) => {
    const s = stateRef.current;
    if (s.cropEditId && (!objectId || s.cropEditId === objectId)) { exit(); return Promise.resolve(false); }
    return enter(objectId);
  }, [stateRef, enter, exit]);

  /** Fit (whole picture inside the frame) / Fill (cover the frame) — the frame stays where it is. */
  const refit = useCallback(async (fit, objectId) => {
    const o = target(objectId);
    if (!o || o.type !== 'image' || !o.properties?.src) return false;
    const nat = await loadNaturalSize(o.properties.src);
    dispatch({ type: 'UPDATE_OBJECT_PROPS', payload: { ids: [o.id], props: { crop: refitCrop(o, nat, fit), objectFit: fit } } });
    return true;
  }, [target, dispatch]);

  /** Publisher "Clear Crop": show the whole picture again by growing the frame. */
  const clear = useCallback((objectId) => {
    const o = target(objectId);
    if (!o || !hasCrop(o.properties)) { toast.info('This picture is not cropped.'); return false; }
    const patch = clearCropPatch(o);
    dispatch({ type: 'UPDATE_OBJECT', payload: { id: o.id, keepCrop: true, data: { x: patch.x, y: patch.y, width: patch.width, height: patch.height, properties: { ...o.properties, crop: patch.crop } } } });
    return true;
  }, [target, dispatch]);

  /** Crop to Shape: clip the frame to a gallery shape (`null` → back to a rectangle). */
  const setShape = useCallback((shapeId, objectId) => {
    const o = target(objectId);
    if (!o || o.type !== 'image') { toast.info('Select a picture first.'); return false; }
    if (o.locked) { toast.info('Unlock the picture to change its shape.'); return false; }
    const next = shapeId && isCropShape(shapeId) ? shapeId : null;
    if ((o.properties?.cropShape || null) === next) return true;
    dispatch({ type: 'UPDATE_OBJECT_PROPS', payload: { ids: [o.id], props: { cropShape: next } } });
    toast.success(next ? `Cropped to ${cropShapeLabel(next)}` : 'Crop shape removed — the picture is a rectangle again', { id: 'crop-shape' });
    return true;
  }, [target, dispatch]);

  /**
   * Crop ▸ Aspect Ratio: enter crop mode if needed, crop the frame to the preset (largest centred rectangle of that
   * ratio inside the frame; the picture stays put) and keep the ratio locked for the crop-handle drags. 'free' unlocks.
   */
  const setAspect = useCallback(async (aspectId, objectId) => {
    const a = cropAspectById(aspectId);
    const o = target(objectId);
    if (!o || o.type !== 'image' || !o.properties?.src) { toast.info('Select a picture first.'); return false; }
    if (stateRef.current.cropEditId !== o.id && !(await enter(o.id))) return false;
    rawDispatch({ type: 'SET_CROP_ASPECT', payload: a.ratio ? a.id : null });
    if (a.ratio) {
      // the crop rectangle entering crop mode just set may not have rendered yet — ensureCrop hands back the same one
      const live = find(o.id) || o;
      const crop = await ensureCrop(live);
      const cur = { ...live, properties: { ...live.properties, crop } };
      const patch = cropToAspect(cur, a.ratio);
      if (patch) dispatch({ type: 'UPDATE_OBJECT', payload: { id: cur.id, keepCrop: true, data: { x: patch.x, y: patch.y, width: patch.width, height: patch.height, properties: { ...cur.properties, crop: patch.crop } } } });
      toast.info(`Aspect ratio ${a.label} — the crop handles keep it; choose Free to drag without it`, { id: 'crop-aspect', duration: 3000 });
    }
    return true;
  }, [target, find, enter, ensureCrop, dispatch, rawDispatch, stateRef]);

  return { cropping: !!state.cropEditId, cropEditId: state.cropEditId, aspect: state.cropAspect || 'free', setAspect, enter, exit, toggle, fit: (id) => refit('contain', id), fill: (id) => refit('cover', id), clear, setShape };
};
